import { useNavigate } from "react-router-dom";
// src/pages/AlertsPage.jsx — v8.8 FINAL
//
// ПАТТЕРН: PagerDuty Incidents + Datadog Event Timeline
// "Каждая строка = доказательство что система спасла деньги"
//
// КЛЮЧЕВЫЕ РЕШЕНИЯ:
// 1. Header: value-statement + live counters по уровням
// 2. CRITICAL строки: красный фон rgba(0.04) — заметно но не агрессивно
// 3. WARNING строки: жёлтый фон rgba(0.025) — средний сигнал
// 4. INFO строки: обычный фон
// 5. Суммарная статистика предотвращённых потерь в header
// 6. Пустое состояние: позитивный сигнал "All stable"

import { useState, useMemo, useEffect } from "react";
import { workerClient } from "../api/workerClient";
import AlertRow from "../components/ui/AlertRow";

function parseTs(ts) {
  if (!ts) return null;
  const n = Number(ts);
  return n > 1e12 ? new Date(n) : new Date(n * 1000);
}

const selectStyle = {
  background: "var(--bg-tertiary)", border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius)", padding: "6px 10px", fontSize: 12,
  color: "var(--text-primary)", outline: "none", cursor: "pointer",
  fontFamily: "var(--font-mono)",
};

const labelStyle = {
  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 5, display: "block",
};

// Счётчик по уровню — Datadog Summary widget паттерн
function LevelPill({ count, label, color }) {
  if (!count) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        minWidth: 24, padding: "2px 7px", borderRadius: "var(--radius-sm)",
        background: `${color}18`, border: `1px solid ${color}35`,
        fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
        color, textAlign: "center",
      }}>
        {count}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
        {label}
      </span>
    </div>
  );
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts,      setAlerts]      = useState([]);
  const [error,       setError]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [levelFilter, setLevelFilter] = useState("all");
  const [sortOrder,   setSortOrder]   = useState("desc");

  useEffect(() => {
    workerClient("/api/alerts")
      .then(res => setAlerts(Array.isArray(res?.alerts) ? res.alerts : []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...alerts];
    if (levelFilter !== "all") {
      list = list.filter(a => (a.level ?? "info").toLowerCase() === levelFilter);
    }
    list.sort((a, b) =>
      sortOrder === "desc"
        ? (b.timestamp ?? 0) - (a.timestamp ?? 0)
        : (a.timestamp ?? 0) - (b.timestamp ?? 0)
    );
    return list;
  }, [alerts, levelFilter, sortOrder]);

  // Счётчики
  const counts = useMemo(() => ({
    critical: alerts.filter(a => (a.level ?? "").toLowerCase() === "critical").length,
    warning:  alerts.filter(a => (a.level ?? "").toLowerCase() === "warning").length,
    info:     alerts.filter(a => (a.level ?? "").toLowerCase() === "info").length,
  }), [alerts]);

  // Диапазон дат
  const dateRange = useMemo(() => {
    if (!alerts.length) return null;
    const sorted = [...alerts].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    const first  = parseTs(sorted[0]?.timestamp);
    const last   = parseTs(sorted[sorted.length - 1]?.timestamp);
    if (!first || !last) return null;
    const fmt = d => d.toLocaleString("en-GB", { day: "2-digit", month: "short" });
    return `${fmt(first)} – ${fmt(last)}`;
  }, [alerts]);

  // Цвет фона строки по уровню
  function rowBg(level) {
    if (level === "critical") return "rgba(240,64,64,0.04)";
    if (level === "warning")  return "rgba(245,166,35,0.025)";
    return "transparent";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ← Back to Analyze */}
      <div data-no-print style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <button
          onClick={() => navigate("/analyze")}
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, cursor: "pointer",
            padding: "4px 10px", borderRadius: "var(--radius-sm)",
            background: "transparent", border: "1px solid var(--border-subtle)",
            color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          ← Analyze
        </button>
      </div>

      {/* ── VALUE HEADER ── */}
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 16,
        paddingBottom: 14, borderBottom: "1px solid var(--border-subtle)",
      }}>
        <div>
          <div style={{
            fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-secondary)",
            marginBottom: 4,
          }}>
            Performance Alerts
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.04em" }}>
            Campaign performance events and risk signals
            {dateRange ? ` · ${dateRange}` : ""}
          </div>
        </div>

        {/* Level counters — сразу видно сколько кризисов */}
        {!loading && alerts.length > 0 && (
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexShrink: 0 }}>
            <LevelPill count={counts.critical} label="Critical" color="var(--danger)" />
            <LevelPill count={counts.warning}  label="Warning"  color="var(--warning)" />
            <LevelPill count={counts.info}     label="Info"     color="var(--accent-primary)" />
          </div>
        )}
      </div>

      {/* ── FILTERS ── */}
      <div style={{
        display: "flex", alignItems: "flex-end", gap: 20,
        padding: "10px 14px",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)",
      }}>
        <div>
          <span style={labelStyle}>Severity</span>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div>
          <span style={labelStyle}>Sort</span>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={selectStyle}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
            {loading ? "…" : `${filtered.length} event${filtered.length !== 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      {/* ── LIST ── */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)", overflow: "hidden",
      }}>
        {loading ? (
          <div style={{ padding: "20px 16px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: "20px 16px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--danger)" }}>Error: {error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "36px 16px", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--success)", marginBottom: 8 }}>
              ✓ All systems stable
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
              No engine interventions needed in this period
            </div>
          </div>
        ) : (
          filtered.map((item, i) => {
            const level = (item.level ?? "info").toLowerCase();
            return (
              <div key={item.id ?? i} style={{
                borderBottom: i < filtered.length - 1 ? "1px solid var(--border-subtle)" : "none",
                background: rowBg(level),
              }}>
                <AlertRow item={item} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


